import { PresetViewSupportedView, type PresetView } from '@bringg/types';
import { action, computed, makeObservable, observable, toJS } from 'mobx';

import { PresetViewBase } from './preset-view-base';
import { AlertTableColumn, AlertTableConfig, AlertTableFilters } from './types';
import { TranslatableOption } from 'bringg-web/types/common.consts';
import { taskAlertColumnsTranslateKeys } from 'bringg-web/features/exceptions-management/columns/task-alert-columns';
import { driverAlertColumnsTranslateKeys } from 'bringg-web/features/exceptions-management/columns/driver-alert-columns';
import { optimizationAlertColumnsTranslateKeys } from 'bringg-web/features/exceptions-management/columns/optimisation-alert-columns';
import { getDefaultColumnOptionsByView } from 'bringg-web/features/exceptions-management/columns/alert-columns-service';

export type PresetViewAlertTableConfig = {
	groupBy: string[];
	filters: Record<string, unknown>;
	sortBy: Record<string, unknown>[];
	additionalFiltersNames: string[];
	fields: Record<string, unknown>[];
};

export class AlertsPresetView extends PresetViewBase {
	_filters: AlertTableConfig['filters'] = null;
	_groupBy: AlertTableConfig['groupBy'] = null;
	_sortBy: AlertTableConfig['sortBy'] = null;
	columnsMap: Map<string, AlertTableColumn> = new Map();
	_columnWidths: Record<string, number> = {};
	_orderedColumns: TranslatableOption[] = [];

	constructor(preset: PresetView) {
		super(preset);

		this.init();

		makeObservable(this, {
			_filters: observable,
			_groupBy: observable,
			_sortBy: observable,
			_columnWidths: observable.ref,
			_orderedColumns: observable.ref,
			columnsMap: observable.ref,
			filters: computed,
			columns: computed,
			orderedColumns: computed,
			columnWidths: computed,
			groupBy: computed,
			sortBy: computed,
			updateFilters: action,
			updateColumns: action,
			updateGroupBy: action,
			updateSortBy: action,
			toggleColumn: action,
			updateColumnsWidth: action,
			updateOrderedColumns: action
		});
	}

	init() {
		this.assignServerToClient();
	}

	assignServerToClient = () => {
		const { view_config, view } = this.serverPresetView;
		const { grouping, sorting, columns } = view_config;
		const filters = this.serverPresetView.view_config.filters as AlertTableFilters;

		this._filters = filters;

		const columnMap = new Map();
		const columnWidths = {};
		const columnOrder = [];

		const columnsWithFallback = columns?.length
			? columns
			: (getDefaultColumnOptionsByView(view).map(item => ({
					...item,
					active: true
			  })) as PresetView['view_config']['columns']);

		columnsWithFallback
			.sort((a, b) => a.position - b.position)
			.forEach(item => {
				const { id, position, active, width } = item;
				// TODO: investigate how to make this check less complex
				let columnTranslationKey = '';
				if (this.serverPresetView.view === PresetViewSupportedView.OrderAlerts) {
					columnTranslationKey = taskAlertColumnsTranslateKeys.get(id);
				} else if (this.serverPresetView.view === PresetViewSupportedView.DriverAlerts) {
					columnTranslationKey = driverAlertColumnsTranslateKeys.get(id);
				} else {
					columnTranslationKey = optimizationAlertColumnsTranslateKeys.get(id);
				}

				columnMap.set(id, {
					...item,
					position: position || 0,
					translateKey: columnTranslationKey,
					translate_key: columnTranslationKey,
					active: Boolean(active)
				});

				if (width) {
					columnWidths[id] = width;
				}

				columnOrder.push({
					id: item.id,
					translateKey: columnTranslationKey
				});
			});

		this.columnsMap = columnMap;
		this._columnWidths = columnWidths;
		this._orderedColumns = columnOrder;

		this._groupBy = grouping || [];
		this._sortBy = sorting || [];
	};

	parseClientToServer = (): PresetView => {
		return {
			...this.serverPresetView,
			view_config: {
				grouping: this.groupBy,
				sorting: this.sortBy,
				columns: this.orderedColumns.map((item, index) => {
					const column = this.columnsMap.get(item.id);

					return {
						...column,
						position: index,
						width: this.columnWidths[item.id] || undefined
					};
				}),
				filters: this.filters
			}
		};
	};

	get filters() {
		return toJS(this._filters);
	}

	// TODO: BRNGG-28641 check alert table filters type according table needs
	// when type defined the error occurred in resetAllFilters of filter-store-adapter.ts
	updateFilters = (value, setTouched = true) => {
		this._filters = value;
		if (setTouched) {
			this.setEdited(true);
		}
	};

	get columns(): AlertTableColumn[] {
		return toJS(Array.from(this.columnsMap.values()));
	}

	updateColumns = (columns: typeof this.columns) => {
		this.columnsMap = new Map(
			columns.map(item => {
				return [item.id, item];
			})
		);
		this.setEdited(true);
	};

	toggleColumn = (id: string, active: boolean) => {
		this.columnsMap.get(id).active = active;
		this.setEdited(true);
	};

	get groupBy() {
		return toJS(this._groupBy);
	}

	updateGroupBy = (value: typeof this.groupBy | null | undefined) => {
		this._groupBy = value;
		this.setEdited(true);
	};

	get sortBy() {
		return toJS(this._sortBy);
	}

	updateSortBy = (value: typeof this.sortBy | null | undefined) => {
		this._sortBy = value;
		this.setEdited(true);
	};

	get columnWidths() {
		return this._columnWidths;
	}

	updateColumnsWidth = (columnWidths: Record<string, number>) => {
		this._columnWidths = columnWidths;
		this.setEdited(true);
	};

	get orderedColumns() {
		return this._orderedColumns;
	}

	updateOrderedColumns = (columns: TranslatableOption[]) => {
		this._orderedColumns = columns;
		this.setEdited(true);
	};

	// TODO: refactor reset column to work with set of hiddenColumn as in table-object-config.ts
	resetColumns = (defaultOrderedColumns: TranslatableOption[]) => {
		this.updateOrderedColumns(defaultOrderedColumns);
		defaultOrderedColumns.forEach(({ id }) =>
			this.columnsMap.set(id, { ...this.columnsMap.get(id), active: true })
		);
	};
}
